import {
  faBan,
  faCheck,
  faChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import { faHandSparkles, faRedo } from '@fortawesome/pro-regular-svg-icons';
import {
  faBolt,
  faCheckCircle,
  faFire,
  faMinusCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import classnames from 'classnames';
import { Heart, Star } from 'lucide-react';
import React from 'react';
import { useQuoteFavoriteProviders } from '../../hooks/useQuoteFavoriteProviders';
import { Img, ImgSizes } from '../Img/Img';
import { ProviderCardCoverCarousel } from './ProviderCardCoverCarousel';
import { ListItem } from '../ListItem/ListItem';
import { ShowIf } from '../ShowIf/ShowIf';
import { IDropShadow } from '../types/dropShadow';
import { IRoundedCorners } from '../types/roundedCorners';

interface IProductCardProps extends IDropShadow, IRoundedCorners {
  coverImg: string;
  coverImgSize?: ImgSizes;
  coverText?: string;
  /**
   * black and transparent overlay that starts from the bottom of cover image
   * */
  overlay?: boolean;
  name: string;
  rating?: number;
  profileImg: string;
  price: number;
  discountedPrice?: number;
  deposit: number;
  depositAltContent?: React.ReactNode;
  status?:
    | 'inDemand'
    | 'unavailable'
    | 'covidTrained'
    | 'requested'
    | 'offered'
    | 'booked'
    | 'cancelled';
  currency?: string;
  totalReviews?: number;
  id?: string;
  data?: any;
  layout?: 'stacked' | 'sideBySide';
  featured?: boolean;
  responsiveCoverImg?: boolean;
  coverImgScreenSizes?: any;
  avatarStaticResponsiveImgSize?: number;
  hideReview?: boolean;
  /** When true, show a "New" label instead of star rating (from API). */
  isNew?: boolean;
  coverImgAspectRatio?: number;
  vaccinated?: boolean;
  onClick?: (e?: any, id?: string, data?: any) => void;
  /** When set with quote/profile ids, image area shows swipeable carousel + favorite. */
  coverImages?: string[];
  quoteId?: string;
  profileId?: string;
  /**
   * Quote marketplace layout: single row with identity left, price + Total right;
   * no chevron, no deposit/travel block in footer.
   */
  /** When true, reduces font sizes and padding for compact 2-column grid display. */
  compact?: boolean;
  footerVariant?: 'default' | 'marketplace';
  /** Label shown below the price. Defaults to "Total". */
  priceLabel?: string;
  /** When true, shows the instant book badge over the cover image. */
  isInstantBook?: boolean;
  /** When true, renders a small "From" label above the price (dynamic slot pricing). */
  showFromPrefix?: boolean;
  /** When true, compact marketplace cards show provider details over the cover image. */
  infoOverlay?: boolean;
  /** When true, shows the "Top Pick" pill over the cover image regardless of dark-card featured styling. */
  topPick?: boolean;
}

export const ProductCard: React.FC<
  React.PropsWithChildren<IProductCardProps>
> = ({
  coverImg,
  overlay,
  shadow = 'none',
  coverText,
  rounded = 'xl',
  status,
  profileImg,
  rating,
  price,
  discountedPrice,
  deposit,
  currency = '$',
  totalReviews,
  name,
  id,
  data,
  layout = 'stacked',
  featured,
  responsiveCoverImg,
  coverImgScreenSizes,
  avatarStaticResponsiveImgSize,
  coverImgAspectRatio = 1.2,
  hideReview,
  isNew,
  depositAltContent,
  vaccinated,
  onClick,
  coverImages,
  quoteId,
  profileId,
  footerVariant = 'default',
  compact = false,
  isInstantBook = false,
  showFromPrefix = false,
  infoOverlay = false,
  topPick = false,
}) => {
  function handleClick(e) {
    onClick?.(e, id, data);
  }

  const { isFavorite, toggleFavorite } = useQuoteFavoriteProviders(quoteId);

  const marketplaceShell =
    footerVariant === 'marketplace' && layout === 'stacked';
  const compactInfoOverlay = marketplaceShell && compact && infoOverlay;

  const imageAspectPadding = { paddingTop: `${coverImgAspectRatio * 100}%` };

  const coverMedia = (
    <>
      {coverImages?.length && quoteId && profileId && !compact ? (
        <ProviderCardCoverCarousel
          images={coverImages}
          quoteId={quoteId}
          profileId={profileId}
          profilePhoto={profileImg}
          profileName={name}
          grayScale={['unavailable', 'cancelled'].includes(status || '')}
          responsive={responsiveCoverImg}
          screenSizes={coverImgScreenSizes}
        />
      ) : (
        <Img
          src={coverImg}
          grayScale={['unavailable', 'cancelled'].includes(status || '')}
          responsive={responsiveCoverImg}
          screenSizes={coverImgScreenSizes}
          hideOnError
          className="absolute top-0 left-0 w-full h-full"
          imgClassName="absolute top-0 left-0 w-full h-full"
        />
      )}
      {overlay && !(marketplaceShell && compact) && (
        <div className="absolute bottom-0 left-0 flex h-1/2 w-full items-center bg-gradient-to-b from-transparent to-black p-3 opacity-50"></div>
      )}
    </>
  );

  return (
    <div
      onClick={handleClick}
      className={classnames(
        'flex w-full flex-col cursor-pointer md:hover:opacity-75',
        {
          [`rounded-${rounded}`]: rounded && typeof rounded === 'string',
          rounded: rounded === true,
          [`shadow-${shadow}`]: shadow && typeof shadow === 'string',
          'shadow-lg': shadow === true,
          'opacity-75': status === 'unavailable',
          'grid grid-cols-2 items-stretch': layout === 'sideBySide',
          'bg-primary p-[2px]': featured,
          'overflow-hidden bg-white': marketplaceShell && !featured,
        }
      )}
    >
      <section className={classnames('relative z-10')}>
        {marketplaceShell ? (
          <>
            <div className={compact ? '' : 'px-3 pt-3'}>
              <div
                className={classnames(
                  'relative overflow-hidden',
                  compact ? `rounded-${rounded}` : 'rounded-2xl',
                  featured && 'bg-black'
                )}
                style={imageAspectPadding}
              >
                {coverMedia}
                {(topPick ||
                  featured ||
                  (compact && !!totalReviews && totalReviews > 200)) && (
                  <div
                    className={classNames(
                      'absolute z-20 flex items-center gap-1.5 rounded-full px-2.5 font-geist text-[10px] font-semibold shadow-sm backdrop-blur-5 pointer-events-none',
                      topPick || featured
                        ? 'top-2 left-2 py-0.5'
                        : 'bottom-2 left-2 py-0.5',
                      topPick || featured
                        ? 'bg-primary text-white border border-primary/20'
                        : 'border border-white/60 bg-white/80 text-gray-900'
                    )}
                  >
                    <Star
                      className={classNames(
                        'h-3 w-3',
                        topPick || featured
                          ? 'fill-white text-white'
                          : 'fill-primary text-primary'
                      )}
                    />
                    {topPick || featured ? (
                      <>
                        <span>Top Pick</span>
                        {!!totalReviews && totalReviews > 200 && (
                          <span className="font-normal text-white/75">
                            · Most reviewed
                          </span>
                        )}
                      </>
                    ) : (
                      <span>Top reviewed</span>
                    )}
                  </div>
                )}
                {isInstantBook && !compact && (
                  <div
                    className={classNames(
                      'absolute top-2 left-2 z-20 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 pointer-events-none',
                      compact ? 'h-8 w-8 justify-center' : 'px-2.5 py-1'
                    )}
                  >
                    <FontAwesomeIcon
                      icon={faBolt}
                      className="text-[11px] text-white"
                    />
                    {!compact && (
                      <span className="text-[10px] font-semibold text-white">
                        Book Instantly
                      </span>
                    )}
                  </div>
                )}
                {!!quoteId &&
                  !!profileId &&
                  (compact || !coverImages?.length) && (
                    <button
                      type="button"
                      className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-sm pointer-events-auto"
                      aria-label={
                        isFavorite(profileId)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                      aria-pressed={isFavorite(profileId)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(profileId, { photo: profileImg, name });
                      }}
                    >
                      <Heart
                        className={classNames(
                          'h-4 w-4',
                          isFavorite(profileId) && 'fill-red-500 text-red-500'
                        )}
                        strokeWidth={isFavorite(profileId) ? 0 : 2}
                      />
                    </button>
                  )}
                {compactInfoOverlay && (
                  <div className="absolute bottom-2 left-2 right-2 z-20 rounded-lg border border-white/10 bg-black/45 px-3 py-2 text-white shadow-sm backdrop-blur-10">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium leading-tight capitalize">
                          {name}
                        </div>
                        <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] leading-tight">
                          {isNew ? (
                            <span className="font-semibold uppercase tracking-wide text-white/80">
                              New
                            </span>
                          ) : (
                            <>
                              {!!rating && (
                                <>
                                  <Star className="h-3 w-3 shrink-0 fill-primary text-primary" />
                                  <span className="font-medium">{rating}</span>
                                </>
                              )}
                              {!!totalReviews && (
                                <span className="truncate text-white/75">
                                  ({totalReviews.toLocaleString()})
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="flex items-baseline justify-end gap-1">
                          {showFromPrefix && (
                            <span className="text-[9px] font-medium leading-none text-white/75">
                              from
                            </span>
                          )}
                          <span className="text-base font-semibold leading-tight tabular-nums">
                            {currency}
                            {(discountedPrice ?? price)?.toLocaleString()}
                          </span>
                        </div>
                        {isInstantBook && (
                          <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] font-medium leading-tight text-white/85">
                            <FontAwesomeIcon
                              icon={faBolt}
                              className="text-[10px]"
                            />
                            <span>Instant book</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!!coverText && (
              <div className="absolute inset-0 flex items-center p-6">
                <div className="ml-auto rounded-md bg-white bg-opacity-40 px-2 py-1 text-white shadow-sm">
                  {coverText}
                  <FontAwesomeIcon className="ml-2" icon={faChevronRight} />
                </div>
              </div>
            )}
            {featured && (
              <section className="absolute top-0 left-0 mt-1 flex w-full justify-end p-2 pr-5 pt-4">
                <span className="mr-1 mt-1 inline-flex items-center rounded-md bg-primary px-3 py-2 text-xs text-white shadow-sm">
                  Recommended
                </span>
              </section>
            )}
          </>
        ) : (
          <>
            <div
              className={classnames(
                {
                  [`rounded-t-xl`]:
                    rounded &&
                    typeof rounded === 'string' &&
                    layout === 'stacked',
                  'rounded-t': rounded === true && layout === 'stacked',
                  [`rounded-l-xl`]:
                    rounded &&
                    typeof rounded === 'string' &&
                    layout === 'sideBySide',
                  'rounded-l': rounded === true && layout === 'sideBySide',
                },
                'overflow-hidden'
              )}
            >
              <div
                className={classnames('pb-2', {
                  'bg-black': featured,
                })}
              >
                <div
                  className="relative overflow-hidden rounded-t-xl"
                  style={imageAspectPadding}
                >
                  {coverMedia}
                </div>
              </div>
              {!!coverText && (
                <div className="absolute top-0 left-0 flex h-full w-full items-center p-3">
                  <div className="ml-auto rounded-md bg-white bg-opacity-40 px-2 py-1 text-white shadow-sm">
                    {coverText}
                    <FontAwesomeIcon className="ml-2" icon={faChevronRight} />
                  </div>
                </div>
              )}
              {featured && (
                <section className="absolute top-0 left-0 mt-1 flex w-full justify-end p-2">
                  <span className="mr-1 mt-1 inline-flex items-center rounded-md bg-primary px-3 py-2 text-xs text-white shadow-sm">
                    Recommended
                  </span>
                </section>
              )}
            </div>
          </>
        )}
        {!compactInfoOverlay &&
          !(footerVariant === 'marketplace' && status === 'offered') && (
            <div
              className={classnames(
                'absolute bottom-0 left-0 z-30 pb-3',
                marketplaceShell && 'px-3'
              )}
            >
              <div className="transform -translate-x-1">
                <ProductStatusLabel status={status} />
              </div>
            </div>
          )}
      </section>
      {!compactInfoOverlay && (
        <footer
          className={classnames('overflow-hidden flex-grow', {
            'bg-white px-3 pb-3 pt-2':
              marketplaceShell && !featured && !compact,
            'bg-white px-2 py-0': marketplaceShell && !featured && compact,
            'px-3 pb-3 pt-2': marketplaceShell && featured && !compact,
            'px-2 py-0': marketplaceShell && featured && compact,
            'p-2': !marketplaceShell,
            'bg-black': featured,
            [`rounded-b-${rounded}`]:
              rounded && typeof rounded === 'string' && layout === 'stacked',
            'rounded-b': rounded === true && layout === 'stacked',
            [`rounded-r-xl`]:
              rounded && typeof rounded === 'string' && layout === 'sideBySide',
            'rounded-r': rounded === true && layout === 'sideBySide',
            'text-white bg-black': featured,
          })}
        >
          {footerVariant === 'marketplace' ? (
            compact ? (
              <div>
                <div className="flex items-start justify-between gap-3 pt-2">
                  <div className="min-w-0 flex-1">
                    <div
                      className={classNames(
                        'font-semibold leading-tight capitalize text-[13.5px]',
                        featured ? 'text-white' : 'text-gray-900'
                      )}
                    >
                      {name}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {discountedPrice != null ? (
                      <div className="flex items-baseline justify-end gap-1">
                        {showFromPrefix && (
                          <div className="leading-none text-gray-400 text-right text-[9px]">
                            From
                          </div>
                        )}
                        <div
                          className={classNames(
                            'font-semibold leading-tight tabular-nums text-[13.5px]',
                            featured ? 'text-white' : 'text-gray-900'
                          )}
                        >
                          {currency}
                          {discountedPrice.toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          'font-semibold leading-tight tabular-nums text-[13.5px]',
                          featured ? 'text-white' : 'text-gray-900'
                        )}
                      >
                        {currency}
                        {price?.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <div
                    className={classNames('leading-tight text-[11px]', {
                      'text-white': featured,
                      'text-gray-900': !featured,
                    })}
                  >
                    <ShowIf condition={!hideReview}>
                      {isNew ? (
                        <span
                          className={classNames(
                            'inline-flex items-center font-semibold uppercase tracking-wide text-[10px] rounded border px-1 py-px',
                            {
                              'border-gray-300 text-gray-500': !featured,
                              'border-white/35 text-gray-300': featured,
                            }
                          )}
                        >
                          New
                        </span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-1">
                          {!!rating && (
                            <>
                              <Star
                                className={classnames(
                                  'inline-block h-3 w-3 fill-current',
                                  {
                                    'text-gray-900':
                                      !featured &&
                                      !['unavailable', 'cancelled'].includes(
                                        status || ''
                                      ),
                                    'text-white':
                                      featured &&
                                      !['unavailable', 'cancelled'].includes(
                                        status || ''
                                      ),
                                    'text-gray-400': [
                                      'unavailable',
                                      'cancelled',
                                    ].includes(status || ''),
                                  }
                                )}
                              />
                              <span
                                className={classNames(
                                  'font-medium text-[11.5px]',
                                  featured ? 'text-white' : 'text-gray-900'
                                )}
                              >
                                {rating}
                              </span>
                            </>
                          )}
                          {!!totalReviews && (
                            <span
                              className={classNames(
                                'text-[11.5px]',
                                featured ? 'text-gray-300' : 'text-gray-500'
                              )}
                            >
                              ({totalReviews.toLocaleString()})
                            </span>
                          )}
                        </div>
                      )}
                    </ShowIf>
                  </div>
                  {isInstantBook && (
                    <div
                      className={classNames(
                        'flex flex-shrink-0 items-center gap-1 font-medium leading-tight text-[11.5px]',
                        featured ? 'text-white' : 'text-gray-900'
                      )}
                    >
                      <FontAwesomeIcon icon={faBolt} className="text-[10px]" />
                      <span>Instant</span>
                    </div>
                  )}
                </div>
                {vaccinated && (
                  <div className="pt-px text-[10px] font-medium uppercase text-green-600">
                    <FontAwesomeIcon icon={faCheckCircle} /> Vaccinated
                  </div>
                )}
              </div>
            ) : (
              <div
                className={classNames(
                  'flex items-start justify-between gap-3',
                  compact ? 'pt-0' : 'pt-1'
                )}
              >
                <div
                  className={classNames(
                    'flex min-w-0 flex-1 items-start',
                    compact ? 'gap-0' : 'gap-2'
                  )}
                >
                  {!compact && (
                    <div className="h-[30px] w-[30px] flex-shrink-0">
                      <Img
                        src={profileImg}
                        size="square"
                        position="top"
                        grayScale={['unavailable', 'cancelled'].includes(
                          status || ''
                        )}
                        responsive={!!avatarStaticResponsiveImgSize}
                        staticResponsiveSize={avatarStaticResponsiveImgSize}
                        hideOnError
                        round
                      />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div
                      className={classNames(
                        'font-semibold leading-tight capitalize',
                        compact ? 'text-[13.5px]' : 'text-[15px]',
                        featured ? 'text-white' : 'text-gray-900'
                      )}
                    >
                      {name}
                    </div>
                    <div
                      className={classNames(
                        'mt-1 leading-tight',
                        compact ? 'text-[11px]' : 'text-[13px]',
                        {
                          'text-white': featured,
                          'text-gray-900': !featured,
                        }
                      )}
                    >
                      <ShowIf condition={!hideReview}>
                        {isNew ? (
                          <span
                            className={classNames(
                              'inline-flex items-center font-semibold uppercase tracking-wide text-[10px] rounded border px-1 py-px',
                              {
                                'border-gray-300 text-gray-500': !featured,
                                'border-white/35 text-gray-300': featured,
                              }
                            )}
                          >
                            New
                          </span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-1">
                            {!!rating && (
                              <>
                                <Star
                                  className={classnames(
                                    'inline-block h-3 w-3 fill-current',
                                    {
                                      'text-gray-900':
                                        !featured &&
                                        !['unavailable', 'cancelled'].includes(
                                          status || ''
                                        ),
                                      'text-white':
                                        featured &&
                                        !['unavailable', 'cancelled'].includes(
                                          status || ''
                                        ),
                                      'text-gray-400': [
                                        'unavailable',
                                        'cancelled',
                                      ].includes(status || ''),
                                    }
                                  )}
                                />
                                <span
                                  className={classNames(
                                    compact
                                      ? 'font-medium text-[11.5px]'
                                      : 'font-medium text-xs',
                                    featured ? 'text-white' : 'text-gray-900'
                                  )}
                                >
                                  {rating}
                                </span>
                              </>
                            )}
                            {!!totalReviews && (
                              <span
                                className={classNames(
                                  compact ? 'text-[11.5px]' : 'text-xs',
                                  featured ? 'text-gray-300' : 'text-gray-500'
                                )}
                              >
                                ({totalReviews.toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                      </ShowIf>
                    </div>
                    {vaccinated && (
                      <div className="pt-px text-[10px] font-medium uppercase text-green-600">
                        <FontAwesomeIcon icon={faCheckCircle} /> Vaccinated
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  {discountedPrice != null ? (
                    <>
                      {showFromPrefix && (
                        <div
                          className={classNames(
                            'leading-none text-gray-400 text-right',
                            compact ? 'text-[9px]' : 'text-[10px]'
                          )}
                        >
                          From
                        </div>
                      )}
                      <div className="flex items-baseline justify-end gap-1">
                        {!compact && (
                          <div
                            className={classNames(
                              'text-xs line-through',
                              featured ? 'text-gray-400' : 'text-gray-400'
                            )}
                          >
                            {currency}
                            {price?.toLocaleString()}
                          </div>
                        )}
                        <div
                          className={classNames(
                            'font-semibold leading-tight tabular-nums',
                            compact ? 'text-[13.5px]' : 'text-lg',
                            featured ? 'text-white' : 'text-gray-900'
                          )}
                        >
                          {currency}
                          {discountedPrice.toLocaleString()}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className={classNames(
                        'font-semibold leading-tight tabular-nums',
                        compact ? 'text-[13.5px]' : 'text-lg',
                        featured ? 'text-white' : 'text-gray-900'
                      )}
                    >
                      {currency}
                      {price?.toLocaleString()}
                    </div>
                  )}
                  {!compact && (
                    <div
                      className={classNames(
                        'mt-0.5 leading-none',
                        compact ? 'text-[10px]' : 'text-[11px]',
                        featured ? 'text-gray-300' : 'text-gray-400'
                      )}
                    >
                      Total
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <>
              <section>
                <ListItem
                  leftIconClassName="-mr-1"
                  leftIcon={
                    <div style={{ width: 30, height: 30 }}>
                      <Img
                        src={profileImg}
                        size="square"
                        position="top"
                        grayScale={['unavailable', 'cancelled'].includes(
                          status || ''
                        )}
                        responsive={!!avatarStaticResponsiveImgSize}
                        staticResponsiveSize={avatarStaticResponsiveImgSize}
                        hideOnError
                        round
                      />
                    </div>
                  }
                  titleClassName={classNames(
                    'font-medium leading-tight text-gray-700 text-xs capitalize',
                    {
                      'text-white': featured,
                      'text-gray-700': !featured,
                    }
                  )}
                  title={name}
                  subTitle={
                    <div
                      className={classNames('text-xs ', {
                        'text-white': featured,
                        'text-gray-700': !featured,
                      })}
                    >
                      <ShowIf condition={!hideReview}>
                        <div>
                          {isNew ? (
                            <span
                              className={classNames(
                                'inline-flex items-center font-semibold uppercase tracking-wide text-[10px] rounded border px-1 py-px',
                                {
                                  'border-gray-300 text-gray-500': !featured,
                                  'border-white/35 text-gray-300': featured,
                                }
                              )}
                            >
                              New
                            </span>
                          ) : (
                            <>
                              {!!rating && rating}
                              {!!rating && (
                                <Star
                                  className={classnames(
                                    'mx-1 inline-block h-3 w-3 fill-current',
                                    {
                                      'text-review-star': ![
                                        'unavailable',
                                        'cancelled',
                                      ].includes(status || ''),
                                      'text-gray-400': [
                                        'unavailable',
                                        'cancelled',
                                      ].includes(status || ''),
                                    }
                                  )}
                                />
                              )}
                              {!!totalReviews && (
                                <span
                                  className={classNames({
                                    'text-gray-600': !featured,
                                    'text-gray-200': featured,
                                  })}
                                >
                                  ({totalReviews.toLocaleString()})
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </ShowIf>
                      {vaccinated && (
                        <div className="pt-px font-medium text-green-600 uppercase">
                          <FontAwesomeIcon icon={faCheckCircle} /> Vaccinated
                        </div>
                      )}
                    </div>
                  }
                  mb="0"
                  pb="0"
                  rightIcon={
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="text-sm text-gray-400"
                    />
                  }
                />
              </section>

              <section className="pt-2 text-sm">
                <div
                  className={classNames('font-medium leading-tight', {
                    'text-white': featured,
                    'text-gray-700': !featured,
                  })}
                >
                  {discountedPrice != null ? (
                    <>
                      <span className="line-through text-gray-400 mr-1 font-normal">
                        {currency}
                        {price?.toLocaleString()}
                      </span>
                      <span className="text-green-600">
                        {currency}
                        {discountedPrice.toLocaleString()}
                      </span>{' '}
                      Total
                    </>
                  ) : (
                    <>
                      {currency}
                      {price?.toLocaleString()} Total
                    </>
                  )}
                </div>
                <div className="leading-none text-gray-400">
                  <ShowIf
                    condition={!depositAltContent}
                    elseTemplate={depositAltContent}
                  >
                    deposit of {currency}
                    {deposit?.toLocaleString()}
                  </ShowIf>
                </div>
              </section>
            </>
          )}
        </footer>
      )}
    </div>
  );
};

const statusIconMapping = {
  inDemand: faFire,
  unavailable: faMinusCircle,
  covidTrained: faHandSparkles,
  requested: faRedo,
  offered: faBolt,
  booked: faCheck,
  cancelled: faBan,
};
const statusLabelClassName =
  'px-8 py-0.5 rounded-l-lg rounded-r-full flex items-center capitalize text-xs';

const statusLabelMapping = {
  inDemand: 'In Demand',
  unavailable: 'Unavailable',
  covidTrained: 'COVID Trained',
  requested: 'Requested',
  offered: 'Instant Book',
  booked: 'booked',
  cancelled: 'cancelled',
};
const ProductStatusLabel: React.FC<
  React.PropsWithChildren<{ status?: string }>
> = ({ status }) => {
  if (!status || !statusLabelMapping[status]) {
    return null;
  }
  return (
    <div
      className={classnames(statusLabelClassName, {
        'bg-red-500 text-white': status === 'inDemand',
        'bg-gray-800 text-white': status === 'unavailable',
        'bg-teal-500 text-white': status === 'covidTrained',
        'bg-secondary-1 text-white': status === 'requested',
        'bg-green-500 text-white': status === 'offered',
        'bg-purple-500 text-white': status === 'booked',
        'bg-gray-200 text-red-500': status === 'cancelled',
      })}
    >
      <FontAwesomeIcon icon={statusIconMapping[status]} className="mr-1" />
      {statusLabelMapping[status]}
    </div>
  );
};
